import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import type { Channel } from '../types';
import * as channelRepository from '../repositories/channelRepository';
import { useUser } from './UserProvider';

interface ChannelContextValue {
  channels: Channel[];
  loading: boolean;
  error: string | null;
  refreshChannels: () => Promise<void>;
}

const ChannelContext = createContext<ChannelContextValue | null>(null);

export function useChannels(): ChannelContextValue {
  const ctx = useContext(ChannelContext);
  if (!ctx) throw new Error('useChannels must be used within ChannelProvider');
  return ctx;
}

export function ChannelProvider({ children }: { children: React.ReactNode }) {
  const { isLoggedIn } = useUser();
  const location = useLocation();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshChannels = useCallback(async () => {
    if (!isLoggedIn) {
      setChannels([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const data = await channelRepository.listChannels();
      setChannels(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch channels');
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (
      location.pathname === '/tldr'
      || location.pathname.startsWith('/search/')
    ) return;
    refreshChannels();
  }, [location.pathname, refreshChannels]);

  return (
    <ChannelContext.Provider value={{
      channels,
      loading,
      error,
      refreshChannels,
    }}>
      {children}
    </ChannelContext.Provider>
  );
}
